import React, { useCallback, useRef, useState } from 'react';
import { AgGridReact } from 'ag-grid-react';
import { ColDef, AllCommunityModule } from 'ag-grid-community';
import { ModuleRegistry, themeBalham } from 'ag-grid-community';
import { format } from 'date-fns';
import * as XLSX from 'xlsx';
import { ReglementClientDTO } from '../../../utils/types/objects/ReglementClientDTO';

ModuleRegistry.registerModules([AllCommunityModule]);

interface PaiementGridProps {
    paiements: ReglementClientDTO[];
}

const PaiementGrid: React.FC<PaiementGridProps> = ({ paiements }) => {
    const gridRef = useRef<AgGridReact>(null);

    const [columnDefs] = useState<ColDef<ReglementClientDTO>[]>([
        { field: 'noRecu', headerName: 'No Reçu', sortable: true, filter: true },
        {
            field: 'datePaiement',
            headerName: 'Date',
            sortable: true,
            valueFormatter: (params) => (params.value ? format(params.value, 'dd/MM/yyyy') : ''),
            filter: 'agDateColumnFilter',
        },
        { field: 'station', headerName: 'Station', sortable: true, filter: true },
        {
            field: 'prixGo',
            headerName: 'Prix GO (USD)',
            sortable: true,
            valueFormatter: (params) => (params.value ? new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(params.value) : ''),
            filter: 'agNumberColumnFilter',
            cellStyle: { textAlign: 'right' },
        },
        {
            field: 'prixEs',
            headerName: 'Prix ES (USD)',
            sortable: true,
            valueFormatter: (params) => (params.value ? new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(params.value) : ''),
            filter: 'agNumberColumnFilter',
            cellStyle: { textAlign: 'right' },
        },
        {
            field: 'montantPaye',
            headerName: 'Montant payé (USD)',
            sortable: true,
            valueFormatter: (params) => (params.value ? new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(params.value) : ''),
            filter: 'agNumberColumnFilter',
            cellStyle: { textAlign: 'right' },
        },
        { field: 'deposant', headerName: 'Déposant', sortable: true, filter: true },
        { field: 'typePaiement', headerName: 'Type paiement', sortable: true, filter: true },
        { field: 'modePaiement', headerName: 'Mode paiement', sortable: true, filter: true },
    ]);

    const myTheme = themeBalham.withParams({
        columnBorder: { style: 'dashed', width: '1px' },
    });

    const exportAsCsv = useCallback(() => {
        if (!gridRef.current) return;

        const visibleColumns = gridRef.current.api.getColumns()?.filter((col) => !col.getColDef().hide);
        if (!visibleColumns || visibleColumns.length === 0) {
            alert('Aucune colonne visible à exporter');
            return;
        }

        const columnKeys = visibleColumns.map((col) => col.getColId());
        const timestamp = new Date().toISOString().replace(/[:.]/g, '-');

        gridRef.current.api.exportDataAsCsv({
            fileName: `paiements_export_${timestamp}.csv`,
            allColumns: false,
            columnKeys,
            suppressQuotes: false,
        });
    }, []);

    const exportAsExcel = useCallback(() => {
        if (!gridRef.current) return;

        const visibleColumns = gridRef.current.api.getColumns()?.filter((col) => !col.getColDef().hide);
        if (!visibleColumns || visibleColumns.length === 0) {
            alert('Aucune colonne visible à exporter');
            return;
        }

        const rowData: ReglementClientDTO[] = [];
        gridRef.current.api.forEachNodeAfterFilterAndSort((node) => rowData.push(node.data));
        if (rowData.length === 0) {
            alert('Aucune donnée à exporter');
            return;
        }

        const exportData = rowData.map((row) => {
            const rowObj: Record<string, any> = {};
            visibleColumns.forEach((col) => {
                const colDef = col.getColDef();
                const field = colDef.field;
                const header = colDef.headerName ?? field ?? '';
                if (!field) return;

                let value = (row as any)[field];
                if (field === 'dateOperation') {
                    value = value ? format(value, 'dd/MM/yyyy') : '';
                }
                rowObj[header] = value;
            });
            return rowObj;
        });

        const worksheet = XLSX.utils.json_to_sheet(exportData);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Paiements');

        const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
        XLSX.writeFile(workbook, `paiements_export_${timestamp}.xlsx`);
    }, []);

    return (
        <>
            <div className="" style={{ height: '100vh', width: '100%' }}>
                <div style={{ height: '100%', width: '100%' }}>
                    <AgGridReact
                        ref={gridRef}
                        theme={myTheme}
                        getRowId={(params) => params.data.id?.toString()}
                        rowData={paiements}
                        columnDefs={columnDefs}
                        pagination={true}
                        domLayout="normal"
                        defaultColDef={{
                            resizable: true,
                            editable: false,
                            minWidth: 100,
                            flex: 1,
                        }}
                        suppressExcelExport={true}
                        enableCellTextSelection={true}
                        ensureDomOrder={true}
                        animateRows={true}
                        overlayNoRowsTemplate="<span style='padding: 10px;'>Aucune donnée ...</span>"
                    />
                </div>
            </div>

            <div className="dropdown mt-1">
                <button className="btn dropdown-toggle" type="button" id="exportDropdown" data-bs-toggle="dropdown">
                    Export
                </button>
                <ul className="dropdown-menu" aria-labelledby="exportDropdown">
                    <li>
                        <button className="dropdown-item" onClick={exportAsCsv}>
                            Export as CSV
                        </button>
                    </li>
                    <li>
                        <button className="dropdown-item" onClick={exportAsExcel}>
                            Export as Excel
                        </button>
                    </li>
                </ul>
            </div>
        </>
    );
};

export default PaiementGrid;
